import { useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { brandName, publicAsset } from "./careData";
import { Footer, Logo } from "../../components/care/CareLayout";

export function PublicNav() {
  return (
    <header className="topbar public-topbar">
      <Logo />
      <nav className="public-nav" aria-label="Main navigation">
        <Link to="/#operations">Care Operations</Link>
        <Link to="/#assessments">Resident Care Processes</Link>
        <Link to="/#medications">Medications</Link>
        <Link to="/#incidents">Incidents</Link>
        <Link to="/#staff">Staff Operations</Link>
        <Link to="/pricing">Pricing</Link>
      </nav>
      <div className="topbar-actions">
        <Link className="btn signin-cta" to="/login"><i className="ti ti-login" /> Sign In</Link>
        <Link className="btn secondary demo-cta" to="/book-a-demo">
          <i className="ti ti-calendar-event" /> Book a Demo
        </Link>
        <Link className="btn primary" to="/get-started">Get Started</Link>
        <Link className="btn mobile-pricing-link" to="/pricing">Pricing</Link>
      </div>
    </header>
  );
}

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  if (!loading && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const offerings = [
    ["ti-users", "One Place for Resident Care", <>Save staff time and reduce administrative costs by keeping resident records, daily care, vital signs, documents, and care teams organized in one compliant workspace.</>, "operations"],
    ["ti-clipboard-check", "Complete Care Work Faster", <>Keep assessments, care plans, behavioral documentation, and recurring reviews complete and review-ready while reducing repeated work and costly documentation gaps.</>, "assessments"],
    ["ti-pill", "Reduce Medication Headaches", <>Use a clear eMAR workflow to save medication-round time, strengthen documentation, and prevent missed follow-up, avoidable errors, and related financial loss.</>, "medications"],
    ["ti-shield-exclamation", "Resolve Incidents with Confidence", <>Move from report to investigation, corrective action, and closure in one place so compliance work takes less time and unresolved incidents do not become costly problems.</>, "incidents"],
    ["ti-calendar-time", "Spend Less Time Managing Staff", <>Coordinate schedules, attendance, clocking, timesheets, and coverage with less manual follow-up so staffing costs stay visible as your AFH business grows.</>, "staff"],
    ["ti-cash", "Stop Payroll Money Leaks", <>Turn approved workforce records into accurate payroll and payment tracking that saves review time, reduces rework, and helps prevent avoidable overpayments.</>, "payroll"],
    ["ti-shield-check", "Stay Compliant and Protect Your Revenue", <>Keep policies, required documents, inventories, emergency drills, and evidence ready before a review to avoid costly gaps, rushed rework, and preventable losses.</>, "compliance"],
    ["ti-building-community", "Grow Without Multiplying the Work", <>Keep every home's care, staff, documents, and compliance records organized so you can add homes without adding the same amount of management work and operating cost.</>, "documents"],
  ];

  return (
    <div className="page public-page">
      <PublicNav />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <h1 className="care-landing-hero-title">Stay compliant, stop the money leaks, save time and grow your AFH business with minimal work and operation costs - with OCarely.</h1>
            <p>{brandName} brings care operations, workforce coordination, and compliance into one AFH workspace. Save time, control costs, prevent compliance gaps from draining money, and run multiple homes without becoming the bottleneck to your business growth.</p>
            <div className="button-row">
              <Link className="btn signin-cta" to="/login"><i className="ti ti-login" /> Sign In</Link>
              <Link className="btn secondary demo-cta" to="/book-a-demo">
                <i className="ti ti-calendar-event" /> Book a Demo
              </Link>
              <Link className="btn primary" to="/get-started">Get Started</Link>
            </div>
          </div>
          <div className="hero-media" aria-label="Caregiver supporting an older adult">
            <img className="hero-photo" src={publicAsset("/care/care.png")} alt="Caregiver supporting an older adult" />
            <div className="floating-note"><i className="ti ti-shield-check" /><span>Save time. Lower costs. Grow without the headaches.</span></div>
          </div>
        </section>

        <section className="public-section" id="operations">
          <h2>Make every AFH easier and more cost-effective to run</h2>
          <p className="public-section-intro">Give your team one clear way to complete daily work while you keep visibility across care, staffing, compliance, and every home you operate.</p>
          <div className="landing-card-grid">
            {offerings.map(([icon, title, text, anchor]) => (
              <Link className="landing-card" to="/get-started" id={anchor === "operations" ? undefined : anchor} key={title}>
                <span className="soft-icon"><i className={`ti ${icon}`} /></span>
                <div><h3>{title}</h3><p>{text}</p><i className="ti ti-arrow-right arrow" /></div>
              </Link>
            ))}
          </div>
          <div className="benefit-strip">
            <div><span><i className="ti ti-building-hospital" /></span><strong>Start Organized</strong><p>Bring resident, staff, care, and compliance information into one workspace to reduce wasted time and unnecessary operating costs.</p></div>
            <div><span><i className="ti ti-calendar-check" /></span><strong>Stop Daily Money Leaks</strong><p>Reduce repeated work, missed follow-up, documentation gaps, payroll rework, and other preventable losses across daily AFH operations.</p></div>
            <div><span><i className="ti ti-sparkles" /></span><strong>Grow Without Becoming the Bottleneck</strong><p>Use clear workflows and multi-home visibility to stay compliant and expand without multiplying management work, costs, or headaches.</p></div>
          </div>
        </section>
      </main>
      <Footer publicLinks />
    </div>
  );
}
